// "Was this analysis right?": a reviewer's thumbs up or down on a finished
// root-cause analysis, with an optional correction (the actual category, the
// actual cause). It goes to the start_rca Lambda's submitRcaFeedback action, which stores it as
// rca.feedback for cd-pipeline-infra's scorecard. Renders nothing until the
// run is done; a verdict on an earlier run is ignored (see currentRcaFeedback).

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ThumbDownOutlined, ThumbUpOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { RcaInfo, RcaVerdict } from '../../types/DeploymentRecord';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { getColors } from '../theme/tokens';
import {
  canRateRca,
  currentRcaFeedback,
  draftFromRcaFeedback,
  MAX_RCA_CAUSE_LENGTH,
  RCA_CATEGORIES,
  rcaFeedbackProblem,
  rcaFeedbackSummary,
  RcaFeedbackDraft,
} from '../lib/rca';

export interface RcaFeedbackControlProps {
  deploymentId: string;
  rca: RcaInfo;
}

interface Notice {
  severity: 'success' | 'error';
  text: string;
}

const emptyDraft: RcaFeedbackDraft = { verdict: null, actualCategory: '', actualCause: '' };

const RcaFeedbackControl: React.FC<RcaFeedbackControlProps> = ({ deploymentId, rca }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { submitRcaFeedback } = useDeploymentData();
  const [draft, setDraft] = useState<RcaFeedbackDraft>(emptyDraft);
  // The correction fields: open after a thumbs-down, or while editing.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  if (!canRateRca(rca)) return null;

  const existing = currentRcaFeedback(rca);
  const problem = rcaFeedbackProblem(draft);

  const send = async (d: RcaFeedbackDraft) => {
    if (!d.verdict) return;
    const cause = d.actualCause.trim();
    setBusy(true);
    try {
      await submitRcaFeedback({
        deploymentId,
        workflowName: rca.workflow_name || '',
        verdict: d.verdict,
        ...(d.categoryCorrect !== undefined ? { categoryCorrect: d.categoryCorrect } : {}),
        ...(d.categoryCorrect === false ? { actualCategory: d.actualCategory } : {}),
        ...(cause ? { actualCause: cause } : {}),
      });
      setNotice({ severity: 'success', text: 'Thanks — feedback saved' });
      setDetailsOpen(false);
      setEditing(false);
    } catch (err) {
      setNotice({ severity: 'error', text: (err as Error).message || 'Failed to save the feedback' });
    } finally {
      setBusy(false);
    }
  };

  const chooseVerdict = (_e: React.MouseEvent, value: RcaVerdict | null) => {
    // Clicking the selected button again deselects it; a verdict stays required.
    if (!value) return;
    const next = { ...draft, verdict: value };
    setDraft(next);
    if (value === 'up' && !detailsOpen) {
      // A bare thumbs-up is the common case: one click, no form.
      void send(next);
      return;
    }
    setDetailsOpen(true);
  };

  const edit = () => {
    if (existing) setDraft(draftFromRcaFeedback(existing));
    setEditing(true);
    setDetailsOpen(true);
  };

  const cancel = () => {
    setDraft(emptyDraft);
    setEditing(false);
    setDetailsOpen(false);
  };

  const categoryAnswer = draft.categoryCorrect === undefined ? null : draft.categoryCorrect ? 'yes' : 'no';

  return (
    // The details dialog can sit inside a clickable row; keep clicks here.
    <Box data-testid="rca-feedback" onClick={e => e.stopPropagation()} sx={{ pt: 0.5 }}>
      {existing && !editing ? (
        <Stack spacing={0.25}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ color: c.inkMuted }}>
              {rcaFeedbackSummary(existing)}
            </Typography>
            <Button size="small" onClick={edit} sx={{ textTransform: 'none', minWidth: 0 }}>
              Edit
            </Button>
          </Box>
          {existing.actual_category && (
            <Typography variant="body2" sx={{ color: c.inkMuted }}>
              Actual category: {existing.actual_category}
            </Typography>
          )}
          {existing.actual_cause && (
            <Typography variant="body2" sx={{ color: c.ink, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {existing.actual_cause}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ color: c.inkSecondary }}>
              Was this analysis right?
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={draft.verdict} onChange={chooseVerdict} disabled={busy}>
              <ToggleButton value="up" aria-label="Helpful" sx={{ py: 0.25 }}>
                <ThumbUpOutlined sx={{ fontSize: 16 }} />
              </ToggleButton>
              <ToggleButton value="down" aria-label="Not helpful" sx={{ py: 0.25 }}>
                <ThumbDownOutlined sx={{ fontSize: 16 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {detailsOpen && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ color: c.inkSecondary }}>
                  Category correct?
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={categoryAnswer}
                  disabled={busy}
                  onChange={(_e, value: 'yes' | 'no' | null) =>
                    setDraft({ ...draft, categoryCorrect: value === null ? undefined : value === 'yes' })
                  }
                >
                  <ToggleButton value="yes" sx={{ py: 0.25, textTransform: 'none' }}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value="no" sx={{ py: 0.25, textTransform: 'none' }}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                {draft.categoryCorrect === false && (
                  <TextField
                    select
                    size="small"
                    label="Actual category"
                    value={draft.actualCategory}
                    disabled={busy}
                    onChange={e => setDraft({ ...draft, actualCategory: e.target.value })}
                    sx={{ minWidth: 160 }}
                  >
                    {RCA_CATEGORIES.map(cat => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>
              <TextField
                multiline
                minRows={2}
                size="small"
                label="What actually caused it? (optional)"
                value={draft.actualCause}
                disabled={busy}
                onChange={e => setDraft({ ...draft, actualCause: e.target.value })}
                slotProps={{ htmlInput: { maxLength: MAX_RCA_CAUSE_LENGTH } }}
              />
              {problem && (
                <Typography variant="caption" sx={{ color: c.inkMuted }}>
                  {problem}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!!problem || busy}
                  onClick={() => void send(draft)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Submit
                </Button>
                <Button size="small" disabled={busy} onClick={cancel} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </Stack>
      )}
      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">
            {notice.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default RcaFeedbackControl;
